/* eslint-disable react-hooks/exhaustive-deps */
import { Box } from '@mui/material'
import { SvgCarretDownIcon, SvgCarretLeftIcon } from 'components/common/LeftPanel/LeftPanel.styled'
import { useActions, useAppSelector } from 'hooks'
import React, { ForwardedRef, forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { comparingCountries } from 'store/AIMetricOT/reducerSlice'
import { Country, CountryData, DownloadType, MetricGroup } from 'types'
import './Table.css'

import * as Flags from 'country-flag-icons/react/3x2'
import { removePrefix } from 'utils/removePrefix'
import CountryNameFlag from './components/CountryNameFlag'
import scrollIndicatorIcon from '../../../assets/images/three-arrows.png'
import ChartHeader from 'components/common/ChartHeader/ChartHeader'
import DownloadImages from 'components/common/Download/DownloadImages'
import { useImageDownload } from 'components/common/Download/useImageDownload'
import { COLOR, DOWNLOAD_FILE_NAME, METRIC_ENDPOINT_RANK, METRIC_ENDPOINT_WEIGHT, PILLAR_INFO } from 'constant'
import {
  calculateCountryDataFromMetricValue,
  calculateCountryMetricWeight,
  calculateMetricGroupForTable,
  calculateWeightedGlobalBarChart,
  generateNationalRankedChartData,
  // calculateCountryMetricScore,
  getCountryVibrancyRank,
  getDataAgainstMetricKey,
  selectCountryData,
  selectDefaultSelectedCountries,
  simplifyMetricGroup,
} from 'utils'
import { formatNumberChart, formatNumberDecimal, formatNumberPureDecimal, formatNumberScores } from 'utils/formatNumber'
import { DoubleRightOutlined } from '@ant-design/icons'

function Table() {
  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
  const metricDataTypeMap = useAppSelector(({ vibrancy }) => vibrancy.metricDataTypeMap)
  const metricGroupFromRedux = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)
  const configuration = useAppSelector(({ vibrancy }) => vibrancy.configuration)
  const metricGroup = calculateMetricGroupForTable(metricGroupFromRedux, { metricValue: configuration.metricValue })
  const yearFilter = useAppSelector(({ vibrancy }) => vibrancy.configuration.yearFilter)
  const renderChartTimes = useAppSelector(({ vibrancy }) => vibrancy.renderChartTimes)
  const selectedMetricValue = useAppSelector(({ vibrancy }) => vibrancy.configuration.metricValue)
  const disabledPillarList = useAppSelector(({ vibrancy }) => vibrancy.disabledPillarList)
  const selectedCountries = useAppSelector(comparingCountries)
  const { setComparingCountries, setShowDwnLoader } = useActions()
  const tableRef = React.createRef<HTMLTableElement>()
  const lastColumnRef = useRef<any>(null)
  const [lastColumnVisible, setLastColumnVisible] = useState<boolean>(false)

  const [expandedMetricGroups, setExpandedMetricGroups] = useState<string[]>(
    metricGroup.map((metric) => metric.metricGroupName),
  )

  // const globalChartData = useMemo<BarChartGlobalData>(() => {
  //   const { metricValue } = configuration
  //   if (metricGroupFromRedux.length === 0 || countryData.length === 0 || yearFilter.yearRange.length === 0) return []

  //   const dataReturn = generateGlobalBarChartData(metricGroupFromRedux, countryData, {
  //     globalYear: yearFilter.currentYear,
  //     metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
  //     metricEndpointRank: METRIC_ENDPOINT_RANK,
  //     metricValue: metricValue,
  //   })
  //   return dataReturn
  // }, [countryData.length, metricGroupFromRedux.length, renderChartTimes])
  const renderChartData = calculateWeightedGlobalBarChart(
    metricGroupFromRedux,
    selectedMetricValue,
    countryData,
    yearFilter.currentYear,
  )

  const tableData = useMemo<Country[]>(() => {
    return calculateCountryDataFromMetricValue(metricGroup, renderChartData, {
      globalYear: yearFilter.currentYear,
      metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
      metricEndpointRank: METRIC_ENDPOINT_RANK,
      metricValue: configuration.metricValue,
    })
  }, [countryData.length, metricGroup.length, renderChartTimes])

  const selectedCountriesData = useMemo<Country[]>(() => {
    return selectCountryData(tableData, selectedCountries)
  }, [selectedCountries, renderChartTimes])

  // useEffect(() => {
  //   selectDefaultSelectedCountries(tableData, setComparingCountries)
  // }, [renderChartTimes])

  // Download images
  const bodyRef = useRef<HTMLDivElement>(null)
  const fileNameDownload = DOWNLOAD_FILE_NAME.globalAiVibrancyRankingTable
  const [clickDownload, setClickDownload] = useState<DownloadType>(null)
  const { handleCallDownload, divRef } = useImageDownload()
  const handleDivDownload = (type: DownloadType) => {
    setShowDwnLoader(true)
    setClickDownload(type)
  }
  useEffect(() => {
    if (clickDownload) {
      handleCallDownload(clickDownload, {
        fileName: fileNameDownload,
        orientation: 'portrait',
        isSquareDownload: true,
      })
      setClickDownload(null)
    }
  }, [clickDownload])

  const toggleMetricGroup = (metricGroupName: string) => {
    setExpandedMetricGroups((prev) =>
      prev.includes(metricGroupName) ? prev.filter((group) => group !== metricGroupName) : [...prev, metricGroupName],
    )
  }

  const calculateIsOpenDropdown = (is4Downloaded: boolean | undefined, metric: MetricGroup) => {
    if (
      metric.multiplier === 0 ||
      disabledPillarList.find((i) => i.metricGroupNameDatakey === metric.metricGroupNameDatakey)
    )
      return false
    return is4Downloaded ? true : expandedMetricGroups.includes(metric.metricGroupName)
  }

  const yearSource = new Date().getFullYear()

  const RenderData: React.FC<{ country: CountryData; metricGroup: any }> = ({ country, metricGroup }) => {
    const data = getDataAgainstMetricKey(country.countryCode, tableData, {
      shortnameScaled: `${metricGroup.metricShortNameScaled}_display` as string,
      shortNameRaw: metricGroup.metricShortName as string,
      currentSelectedYear: configuration.yearFilter.currentYear,
      metricType: selectedMetricValue,
    })
    const isPercentage = metricDataTypeMap[metricGroup.metricShortName] === 'Percentage'
    let value: string | number = '-'
    if (data?.value?.toString() !== 'NaN' && !isPercentage) {
      value = data?.value ? formatNumberChart(data?.value, 2) : '-*'
    } else if (data?.value?.toString() !== 'NaN' && isPercentage) {
      value = data?.value
        ? formatNumberChart(data.value * 100, 2) +
          // (data.value * 100).toFixed(2)
          '%'
        : '-*'
    }
    if (data?.value === 0) {
      value = `0${isPercentage ? '%' : ''}`
    }
    // const rank = data?.ranking?.toString() !== 'NaN' ? data?.ranking : '-*'
    // const ranking = `${rank}/${data?.countCountry}`

    const dataRank = generateNationalRankedChartData(metricGroupFromRedux, countryData, {
      globalYear: yearFilter?.currentYear,
      metricName: metricGroup.metricName,
    })
    const countryDataRank = dataRank?.find((el) => el.countryName === country.countryName)
    const gRank = countryDataRank ? countryDataRank.cRank : 0
    const ranking = `${gRank}/${countryDataRank ? dataRank.length : 0}`

    return (
      <>
        <td>{value || '-*'}</td>
        <td>
          {data?.score || data?.score === 0
            ? data?.score?.toString() !== 'NaN'
              ? formatNumberScores(data?.score)
              : '-*'
            : '-*'}
        </td>
        <td>{value === '-*' ? '-*' : ranking}</td>
      </>
    )
  }

  useEffect(() => {
    // Update the last column ref after the component renders or updates
    if (tableRef.current) {
      const lastColumnCells = tableRef.current.querySelectorAll('thead > tr > td:last-child, tr > th:last-child')
      lastColumnRef.current = lastColumnCells[lastColumnCells.length - 1]

      if (lastColumnRef.current) {
        // Create an IntersectionObserver for the last column cell
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              setLastColumnVisible(entry.isIntersecting)
            })
          },
          {
            root: null,
            threshold: 0.5,
          },
        )

        observer.observe(lastColumnRef.current)

        // Cleanup the observer when the component unmounts
        return () => {
          if (lastColumnRef.current) observer.unobserve(lastColumnRef.current)
        }
      }
    }
  }, [selectedCountriesData]) // Re-run effect whenever data changes

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ zIndex: -1, position: 'absolute', padding: '24px' }} ref={divRef}>
          <ChartHeader
            header1={`${configuration.yearFilter.currentYear} Global AI Vibrancy Ranking`}
            header2={`Weighted Index Score | Source: ${yearSource} AI Index`}
          />
          <br />
          <div className='new-table-container downloaded-table' style={{ marginTop: '10px', fontSize: '0.875rem' }}>
            <table
              border={1}
              style={{
                borderCollapse: 'separate',
                border: 'none',
                borderSpacing: 0,
              }}
            >
              <thead>
                <tr>
                  <th colSpan={2}></th>
                  {selectedCountriesData?.map((ele) => {
                    return (
                      <th key={ele.countryCode} colSpan={3}>
                        <CountryNameFlag
                          countryCode={ele.countryCode as keyof typeof Flags}
                          countryName={ele.countryName}
                        />
                      </th>
                    )
                  })}
                </tr>
                <tr>
                  <th>{selectedMetricValue === 'absolute' ? 'Absolute Values' : 'Per Capita Values'}</th>
                  <th>
                    Weight<sup>**</sup>
                  </th>
                  {selectedCountries?.map((el) => (
                    <React.Fragment key={el}>
                      <th>Value</th>
                      <th>Score</th>
                      <th>Rank</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {metricGroup.map((metric) => {
                  const simplifiedMetricGroup = simplifyMetricGroup(metric, selectedMetricValue)
                  const isOpenDropdown = calculateIsOpenDropdown(true, metric)
                  return (
                    <>
                      <tr
                        key={metric.metricGroupName}
                        className={`expandingRow ${isOpenDropdown ? '' : 'collapsedRow'}`}
                      >
                        <td className='hasExpandingFeature'>
                          <p>
                            <span>
                              {PILLAR_INFO.find((pillar) => pillar.name === metric.metricGroupName)!.displayName}
                            </span>
                            <span
                              className='table-btn-dropdown'
                              onClick={() => toggleMetricGroup(metric.metricGroupName)}
                            >
                              {isOpenDropdown ? (
                                <SvgCarretLeftIcon iconColor={COLOR.DARK} width='14px' height='14px' />
                              ) : (
                                <SvgCarretDownIcon iconColor={COLOR.DARK} width='18px' height='18px' />
                              )}
                            </span>
                          </p>
                        </td>
                        <td>{isOpenDropdown ? `${formatNumberDecimal(metric.multiplier * 100)}%` : ''}</td>
                        {selectedCountriesData.map((element) => {
                          const metricScore = calculateCountryMetricWeight(element.countryCode, tableData)
                          const score = metricScore[metric.metricGroupNameDatakey]
                          return (
                            <React.Fragment key={element.countryName}>
                              <td></td>
                              <td>{isOpenDropdown ? formatNumberPureDecimal(score || 0, 2) : ''}</td>
                              <td></td>
                            </React.Fragment>
                          )
                        })}
                      </tr>
                      {isOpenDropdown && (
                        <>
                          {simplifiedMetricGroup?.map((element) => {
                            const cutMetricName = removePrefix(element.metricName, 'Number of ')
                            return (
                              <tr>
                                <td>
                                  <p className='td-metric-name'>{cutMetricName}</p>
                                </td>
                                <td>{formatNumberDecimal(element.multiplier * 100)}%</td>
                                {selectedCountriesData?.map((country) => (
                                  <RenderData country={country} metricGroup={element} />
                                ))}
                              </tr>
                            )
                          })}
                        </>
                      )}
                    </>
                  )
                })}
                <tr>
                  <td colSpan={2}>
                    <p>
                      Vibrancy Score and Ranking{' '}
                      <span className='text-small'>(out of {countryData.length} countries)</span>
                    </p>
                  </td>
                  {selectedCountriesData.map((ele) => {
                    const rank = getCountryVibrancyRank(ele.countryCode, tableData)
                    const metricScore = calculateCountryMetricWeight(ele.countryCode, tableData)
                    const totalScore = Object.values(metricScore as Record<string, number>)?.reduce(
                      (acc: any, value: number) => Number(acc || 0) + value,
                      0,
                    )
                    return (
                      <React.Fragment key={ele.countryName}>
                        <td></td>
                        <td>{formatNumberDecimal(totalScore, 2)}</td>
                        <td>
                          <span style={{ letterSpacing: '1px' }}>{rank}</span>/{countryData.length}
                        </td>
                      </React.Fragment>
                    )
                  })}
                </tr>
              </tbody>
            </table>
          </div>
          <span className='table-text-footer'>
            * Missing values are imputed using yearly medians across countries for each indicator to compute weighted
            index scores. Indicators unavailable for a given year across all countries are excluded, and their weights
            are proportionally redistributed among the remaining indicators.
          </span>
          <br />
          <span className='table-text-footer'>
            ** The weights assigned to pillars and individual metrics can be adjusted in the interactive Global view,
            which ranks countries.
          </span>
        </Box>
      </Box>
      <div className='table-container'>
        <DownloadImages
          handleDownPNG={() => handleDivDownload('png')}
          handleDownJPG={() => handleDivDownload('jpg')}
          handleDownPDF={() => handleDivDownload('pdf')}
        />
        <div className='table-stable'>
          <ChartHeader
            header1={`${configuration.yearFilter.currentYear} Global AI Vibrancy Ranking`}
            header2={`Weighted Index Score | Source: ${yearSource} AI Index`}
          />
          <br />
          <div className='scrollableContent'>
            {!lastColumnVisible && (
              <div className='scrollToRightIndicator'>
                <img src={scrollIndicatorIcon} alt='Scroll Right Indicator' />
              </div>
            )}
            <div className='new-table-container' style={{ marginTop: '22px', fontSize: '0.875rem' }}>
              <table
                border={1}
                style={{
                  borderCollapse: 'separate',
                  border: 'none',
                  borderSpacing: 0,
                }}
                ref={tableRef}
              >
                <thead style={{ zIndex: '95' }}>
                  <tr>
                    <th colSpan={2}></th>
                    {selectedCountriesData?.map((ele, idx) => {
                      return (
                        <th key={ele.countryCode} colSpan={3}>
                          <CountryNameFlag
                            countryCode={ele.countryCode as keyof typeof Flags}
                            countryName={ele.countryName}
                          />
                        </th>
                      )
                    })}
                  </tr>
                  <tr>
                    <th>{selectedMetricValue === 'absolute' ? 'Absolute Values' : 'Per Capita Values'}</th>
                    <th>
                      Weight<sup>**</sup>
                    </th>
                    {selectedCountries?.map((el) => (
                      <React.Fragment key={el}>
                        <th>Value</th>
                        <th>Score</th>
                        <th>Rank</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {metricGroup.map((metric) => {
                    const simplifiedMetricGroup = simplifyMetricGroup(metric, selectedMetricValue)
                    const isOpenDropdown = calculateIsOpenDropdown(false, metric)
                    return (
                      <>
                        <tr
                          key={metric.metricGroupName}
                          className={`expandingRow ${isOpenDropdown ? '' : 'collapsedRow'}`}
                        >
                          <td className='hasExpandingFeature'>
                            <p>
                              <span>
                                {PILLAR_INFO.find((pillar) => pillar.name === metric.metricGroupName)!.displayName}
                              </span>
                              <span
                                className='table-btn-dropdown'
                                onClick={() => toggleMetricGroup(metric.metricGroupName)}
                              >
                                {isOpenDropdown ? (
                                  <SvgCarretLeftIcon iconColor={COLOR.DARK} width='14px' height='14px' />
                                ) : (
                                  <SvgCarretDownIcon iconColor={COLOR.DARK} width='18px' height='18px' />
                                )}
                              </span>
                            </p>
                          </td>
                          <td>{isOpenDropdown ? `${formatNumberDecimal(metric.multiplier * 100)}%` : ''}</td>
                          {selectedCountriesData.map((element) => {
                            const metricScore = calculateCountryMetricWeight(element.countryCode, tableData)
                            const score = metricScore[metric.metricGroupNameDatakey]
                            return (
                              <React.Fragment key={element.countryName}>
                                <td></td>
                                <td>{isOpenDropdown ? formatNumberPureDecimal(score || 0, 2) : ''}</td>
                                <td></td>
                              </React.Fragment>
                            )
                          })}
                        </tr>
                        {isOpenDropdown && (
                          <>
                            {simplifiedMetricGroup?.map((element) => {
                              const cutMetricName = removePrefix(element.metricName, 'Number of ')
                              return (
                                <tr>
                                  <td>
                                    <p className='td-metric-name'>{cutMetricName}</p>
                                  </td>
                                  <td>{formatNumberDecimal(element.multiplier * 100)}%</td>
                                  {selectedCountriesData?.map((country) => (
                                    <RenderData country={country} metricGroup={element} />
                                  ))}
                                </tr>
                              )
                            })}
                          </>
                        )}
                      </>
                    )
                  })}
                  <tr>
                    <td colSpan={2}>
                      <p>
                        Vibrancy Score and Ranking{' '}
                        <span className='text-small'>(out of {countryData.length} countries)</span>
                      </p>
                    </td>
                    {selectedCountriesData.map((ele) => {
                      const rank = getCountryVibrancyRank(ele.countryCode, tableData)
                      const metricScore = calculateCountryMetricWeight(ele.countryCode, tableData)
                      const totalScore = Object.values(metricScore as Record<string, number>)?.reduce(
                        (acc: any, value: number) => Number(acc || 0) + value,
                        0,
                      )
                      return (
                        <React.Fragment key={ele.countryName}>
                          <td></td>
                          <td>{formatNumberDecimal(totalScore, 2)}</td>
                          <td>
                            <span style={{ letterSpacing: '1px' }}>{rank}</span>/{countryData.length}
                          </td>
                        </React.Fragment>
                      )
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div style={{ fontSize: '0.875rem' }}>
          <p>
            * Missing values are imputed using yearly medians across countries for each indicator to compute weighted
            index scores. Indicators unavailable for a given year across all countries are excluded, and their weights
            are proportionally redistributed among the remaining indicators.
          </p>
          <p>
            ** The weights assigned to pillars and individual metrics can be adjusted in the interactive Global view,
            which ranks countries.
          </p>
        </div>
      </div>
    </>
  )
}

export default Table
